import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { apiService } from "../../api/services/apiService";
import { useBookmarkableTab } from "../../hooks/useBookmarkableTab";
import type { PlatformTenant, TenantMember } from "../../types";
import { TenantStatus } from "../../types";

const TENANT_MANAGEMENT_TAB_SLUGS = ["organization", "admins"] as const;
const STATUS_CHANGE_PROTECTED_TENANT_IDS = new Set(["sidgs-platform"]);

const isTenantStatusChangeProtected = (tenantId: string) =>
  STATUS_CHANGE_PROTECTED_TENANT_IDS.has(tenantId);

const tablePaperSx = {
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
} as const;

interface TenantManagementPanelProps {
  onMessages?: (messages: { error?: string | null; success?: string | null }) => void;
}

const TenantManagementPanel: React.FC<TenantManagementPanelProps> = ({ onMessages }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTenantId = searchParams.get("tenant");
  const { tabIndex: tab, setTabIndex: setTab } = useBookmarkableTab({
    slugs: TENANT_MANAGEMENT_TAB_SLUGS,
  });
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [orgLoading, setOrgLoading] = useState(false);
  const [orgSaving, setOrgSaving] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [address, setAddress] = useState("");
  const [website, setWebsite] = useState("");
  const [phone, setPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  const [adminsLoading, setAdminsLoading] = useState(false);
  const [admins, setAdmins] = useState<TenantMember[]>([]);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminDisplayName, setAdminDisplayName] = useState("");
  const [adminSaving, setAdminSaving] = useState(false);

  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [newTenantId, setNewTenantId] = useState("");
  const [newTenantDisplayName, setNewTenantDisplayName] = useState("");
  const [tenantSaving, setTenantSaving] = useState(false);

  const reportMessages = (nextError: string | null, nextSuccess: string | null) => {
    setError(nextError);
    setSuccess(nextSuccess);
    onMessages?.({ error: nextError, success: nextSuccess });
  };

  const clearMessages = () => reportMessages(null, null);

  const loadTenants = useCallback(() => {
    setTenantsLoading(true);
    apiService
      .getPlatformTenants()
      .then(setTenants)
      .catch((err: unknown) =>
        reportMessages(
          err instanceof Error ? err.message : "Failed to load tenants",
          null,
        ),
      )
      .finally(() => setTenantsLoading(false));
  }, []);

  const loadOrganization = (tenantId: string) => {
    setOrgLoading(true);
    apiService
      .getPlatformTenantOrganizationSettings(tenantId)
      .then((s) => {
        setCompanyName(s.companyName ?? "");
        setAddress(s.address ?? "");
        setWebsite(s.website ?? "");
        setPhone(s.phone ?? "");
        setContactEmail(s.contactEmail ?? "");
      })
      .catch((err: unknown) =>
        reportMessages(
          err instanceof Error ? err.message : "Failed to load organization settings",
          null,
        ),
      )
      .finally(() => setOrgLoading(false));
  };

  const loadAdmins = (tenantId: string) => {
    setAdminsLoading(true);
    apiService
      .getPlatformTenantAdmins(tenantId)
      .then(setAdmins)
      .catch((err: unknown) =>
        reportMessages(
          err instanceof Error ? err.message : "Failed to load tenant admins",
          null,
        ),
      )
      .finally(() => setAdminsLoading(false));
  };

  const loadTenantData = (tenantId: string) => {
    clearMessages();
    loadOrganization(tenantId);
    loadAdmins(tenantId);
  };

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    if (!activeTenantId) return;
    loadTenantData(activeTenantId);
  }, [activeTenantId]);

  const handleSelectTenant = (tenantId: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tenant", tenantId);
        next.set("tab", TENANT_MANAGEMENT_TAB_SLUGS[0]);
        return next;
      },
      { replace: true },
    );
  };

  const handleCreateTenant = async () => {
    const trimmedId = newTenantId.trim();
    if (!trimmedId) {
      reportMessages("Tenant ID is required.", null);
      return;
    }
    setTenantSaving(true);
    clearMessages();
    try {
      const created = await apiService.createPlatformTenant({
        tenantId: trimmedId,
        displayName: newTenantDisplayName.trim() || undefined,
      });
      reportMessages(null, `Tenant "${created.tenantId}" created.`);
      setTenantDialogOpen(false);
      setNewTenantId("");
      setNewTenantDisplayName("");
      loadTenants();
      handleSelectTenant(created.tenantId);
    } catch (err: unknown) {
      reportMessages(err instanceof Error ? err.message : "Failed to create tenant", null);
    } finally {
      setTenantSaving(false);
    }
  };

  const handleToggleTenantStatus = async (tenant: PlatformTenant) => {
    if (isTenantStatusChangeProtected(tenant.tenantId)) {
      return;
    }
    const enabled = tenant.status !== TenantStatus.ACTIVE;
    setStatusUpdating(tenant.tenantId);
    clearMessages();
    try {
      const updated = await apiService.setPlatformTenantStatus(tenant.tenantId, enabled);
      setTenants((prev) =>
        prev.map((t) => (t.tenantId === updated.tenantId ? updated : t)),
      );
      reportMessages(
        null,
        enabled
          ? `Tenant "${tenant.tenantId}" enabled.`
          : `Tenant "${tenant.tenantId}" disabled.`,
      );
    } catch (err: unknown) {
      reportMessages(
        err instanceof Error ? err.message : "Failed to update tenant status",
        null,
      );
    } finally {
      setStatusUpdating(null);
    }
  };

  const handleSaveOrganization = async () => {
    if (!activeTenantId) return;
    setOrgSaving(true);
    clearMessages();
    try {
      await apiService.updatePlatformTenantOrganizationSettings(activeTenantId, {
        companyName,
        address,
        website,
        phone,
        contactEmail,
      });
      reportMessages(null, "Organization settings saved.");
      loadTenants();
    } catch (err: unknown) {
      reportMessages(
        err instanceof Error ? err.message : "Failed to save organization settings",
        null,
      );
    } finally {
      setOrgSaving(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!activeTenantId) return;
    setAdminSaving(true);
    clearMessages();
    try {
      await apiService.addPlatformTenantAdmin(activeTenantId, {
        email: adminEmail,
        displayName: adminDisplayName || undefined,
      });
      reportMessages(null, "Tenant admin added.");
      setAdminDialogOpen(false);
      setAdminEmail("");
      setAdminDisplayName("");
      loadAdmins(activeTenantId);
    } catch (err: unknown) {
      reportMessages(err instanceof Error ? err.message : "Failed to add tenant admin", null);
    } finally {
      setAdminSaving(false);
    }
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3, ...tablePaperSx }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Tenants
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Only enabled tenants can be selected at login. Select a tenant to manage its
              settings.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setNewTenantId("");
              setNewTenantDisplayName("");
              setTenantDialogOpen(true);
            }}
          >
            Add tenant
          </Button>
        </Box>

        {tenantsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tenant ID</TableCell>
                  <TableCell>Display name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No tenants registered yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tenants.map((tenant) => {
                    const isActive = tenant.status === TenantStatus.ACTIVE;
                    const isSelected = activeTenantId === tenant.tenantId;
                    const isStatusProtected = isTenantStatusChangeProtected(tenant.tenantId);
                    return (
                      <TableRow
                        key={tenant.tenantId}
                        hover
                        selected={isSelected}
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleSelectTenant(tenant.tenantId)}
                      >
                        <TableCell>{tenant.tenantId}</TableCell>
                        <TableCell>{tenant.displayName ?? "—"}</TableCell>
                        <TableCell>
                          <Chip
                            label={isActive ? "Enabled" : "Disabled"}
                            color={isActive ? "success" : "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            color={isActive ? "warning" : "success"}
                            disabled={
                              isStatusProtected || statusUpdating === tenant.tenantId
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleToggleTenantStatus(tenant);
                            }}
                          >
                            {isStatusProtected
                              ? "Protected"
                              : statusUpdating === tenant.tenantId
                                ? "Updating..."
                                : isActive
                                  ? "Disable"
                                  : "Enable"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {!activeTenantId ? (
        <Alert severity="info">
          Select a tenant from the list above to manage organization settings and tenant admins.
        </Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Managing tenant: <strong>{activeTenantId}</strong>
          </Typography>

          <Tabs value={tab} onChange={setTab} sx={{ mb: 3 }}>
            <Tab label="Organization" />
            <Tab label="Tenant admins" />
          </Tabs>

          {tab === 0 && (
            <Paper sx={{ p: 3, ...tablePaperSx }}>
              {orgLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Organization contact
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    These fields appear in the site footer for this tenant.
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                      label="Company name"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                    <TextField
                      label="Website"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Contact email"
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      fullWidth
                    />
                  </Box>
                  <Button
                    variant="contained"
                    sx={{ mt: 3 }}
                    onClick={handleSaveOrganization}
                    disabled={orgSaving}
                  >
                    {orgSaving ? "Saving..." : "Save organization"}
                  </Button>
                </>
              )}
            </Paper>
          )}

          {tab === 1 && (
            <Paper sx={{ p: 3, ...tablePaperSx }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Tenant admins</Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => {
                    setAdminEmail("");
                    setAdminDisplayName("");
                    setAdminDialogOpen(true);
                  }}
                >
                  Add tenant admin
                </Button>
              </Box>

              {adminsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Display name</TableCell>
                        <TableCell>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {admins.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                              No tenant admins for this tenant yet.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        admins.map((admin) => (
                          <TableRow key={admin.email}>
                            <TableCell>{admin.email}</TableCell>
                            <TableCell>{admin.displayName ?? "—"}</TableCell>
                            <TableCell>{admin.createdOn ?? "—"}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          )}
        </>
      )}

      <Dialog
        open={tenantDialogOpen}
        onClose={() => !tenantSaving && setTenantDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add tenant</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Tenant ID"
              value={newTenantId}
              onChange={(e) => setNewTenantId(e.target.value)}
              required
              fullWidth
              helperText="3–64 characters: letters, numbers, hyphens, underscores"
            />
            <TextField
              label="Display name"
              value={newTenantDisplayName}
              onChange={(e) => setNewTenantDisplayName(e.target.value)}
              fullWidth
              helperText="Optional; defaults to tenant ID if left blank"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTenantDialogOpen(false)} disabled={tenantSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateTenant}
            disabled={tenantSaving || !newTenantId.trim()}
          >
            {tenantSaving ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={adminDialogOpen}
        onClose={() => !adminSaving && setAdminDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add tenant admin</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Display name"
              value={adminDisplayName}
              onChange={(e) => setAdminDisplayName(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminDialogOpen(false)} disabled={adminSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddAdmin}
            disabled={adminSaving || !adminEmail.trim()}
          >
            {adminSaving ? "Adding..." : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TenantManagementPanel;
